import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Building2,
  KeyRound,
  Ban,
  MessageSquareText,
  Calendar,
  Bot,
  UserCheck,
  HandCoins,
  FileText,
  BellRing,
  GraduationCap,
  BrainCircuit,
  Cpu,
  Bell,
  Clock3,
  MapPin,
} from "lucide-react";
import { useAuth } from "../../../hooks/use-auth";
import { BackButton } from "../../../components/button/back-button";
import {
  APP_RESOURCES,
  AccessControl,
  type AppResource,
} from "../../../permissions/access-control";
import type { JSX } from "react";

type Itens = {
  nome: string;
  icone: JSX.Element;
  rota: string;
  resource: AppResource;
  secao: "Cadastros" | "Inteligência Artificial" | "Agendamento" | "Geral";
};

const secoes: Array<Itens["secao"]> = [
  "Cadastros",
  "Inteligência Artificial",
  "Agendamento",
  "Geral",
];

export default function Configuracoes() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const itens: Array<Itens> = useMemo(() => {
    return [
      {
        nome: "Tenants",
        icone: <Building2 className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/tenant",
        resource: APP_RESOURCES.CONFIG_TENANT,
        secao: "Cadastros",
      },
      {
        nome: "Usuários do sistema",
        icone: <User className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/system-user",
        resource: APP_RESOURCES.CONFIG_SYSTEM_USER,
        secao: "Cadastros",
      },
      {
        nome: "Atendentes logados",
        icone: <UserCheck className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/logged-attendants",
        resource: APP_RESOURCES.CONFIG_LOGGED_ATTENDANTS,
        secao: "Geral",
      },
      {
        nome: "Custos por tenant",
        icone: <HandCoins className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/tenant-charge",
        resource: APP_RESOURCES.CONFIG_TENANT_CHARGE,
        secao: "Geral",
      },
      {
        nome: "Contatos bloqueados",
        icone: <Ban className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/blocked-contacts",
        resource: APP_RESOURCES.CONFIG_BLOCKED_CONTACTS,
        secao: "Cadastros",
      },
      {
        nome: "Mensagens pré-definidas",
        icone: <MessageSquareText className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/predefined-messages",
        resource: APP_RESOURCES.CONFIG_PREDEFINED_MESSAGES,
        secao: "Cadastros",
      },
      {
        nome: "Notificações",
        icone: <Bell className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/notifications",
        resource: APP_RESOURCES.CONFIG_NOTIFICATIONS,
        secao: "Cadastros",
      },
      {
        nome: "Localização do estabelecimento",
        icone: <MapPin className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/establishment-location",
        resource: APP_RESOURCES.CONFIG_ESTABLISHMENT_LOCATION,
        secao: "Geral",
      },
      {
        nome: "Janela de atendimento",
        icone: <Clock3 className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/attendance-window",
        resource: APP_RESOURCES.CONFIG_ATTENDANCE_WINDOW,
        secao: "Geral",
      },
      {
        nome: "Agenda semanal",
        icone: <Calendar className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/weekly-schedule",
        resource: APP_RESOURCES.CONFIG_WEEKLY_SCHEDULE,
        secao: "Agendamento",
      },
      {
        nome: "Configurações de agendamento",
        icone: <BellRing className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/appointment-settings",
        resource: APP_RESOURCES.CONFIG_APPOINTMENT_REMINDERS,
        secao: "Agendamento",
      },
      {
        nome: "Configurações de IA",
        icone: <Bot className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/ai-tenant",
        resource: APP_RESOURCES.CONFIG_AI_TENANT,
        secao: "Inteligência Artificial",
      },
      {
        nome: "Skills de IA",
        icone: <BrainCircuit className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/ai-skills",
        resource: APP_RESOURCES.CONFIG_AI_SKILLS,
        secao: "Inteligência Artificial",
      },
      {
        nome: "Modelos de IA",
        icone: <Cpu className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/ai-models",
        resource: APP_RESOURCES.CONFIG_AI_MODELS,
        secao: "Inteligência Artificial",
      },
      {
        nome: "Configurações de RAG",
        icone: <FileText className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/rag-tenant",
        resource: APP_RESOURCES.CONFIG_RAG_TENANT,
        secao: "Inteligência Artificial",
      },
      {
        nome: "Treinamentos",
        icone: <GraduationCap className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/training-videos",
        resource: APP_RESOURCES.CONFIG_TRAINING_VIDEOS,
        secao: "Geral",
      },
      {
        nome: "Alterar senha",
        icone: <KeyRound className="w-8 h-8 mb-2 text-slate-700" />,
        rota: "/painel/config/password-change",
        resource: APP_RESOURCES.CONFIG_PASSWORD_CHANGE,
        secao: "Geral",
      },
    ];
  }, []);

  const itensVisiveis = useMemo(
    () =>
      user?.role
        ? itens.filter((item) =>
            AccessControl.canView(item.resource, user.role),
          )
        : [],
    [itens, user?.role],
  );

  return (
    <div className="app-page-compact">
      <BackButton />
      <h1 className="app-page-title mb-6">Configurações</h1>

      <div className="space-y-7">
        {secoes.map((secao) => {
          const itensDaSecao = itensVisiveis.filter(
            (item) => item.secao === secao,
          );
          if (itensDaSecao.length === 0) return null;

          return (
            <section key={secao} aria-labelledby={`secao-${secao}`}>
              <h2
                id={`secao-${secao}`}
                className="mb-4 border-b border-slate-200/70 pb-2 text-xs font-medium uppercase tracking-wider text-slate-400"
              >
                {secao}
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-6 md:grid-cols-4">
                {itensDaSecao.map((item) => (
                  <div
                    key={item.rota}
                    onClick={() => navigate(item.rota)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(item.rota);
                      }
                    }}
                    role="link"
                    tabIndex={0}
                    className="app-card min-w-0 cursor-pointer px-2 py-5 text-center transition hover:-translate-y-0.5 hover:shadow-soft sm:mx-2 sm:p-6 [&>svg]:mx-auto"
                  >
                    {item.icone}
                    <span className="block break-words text-sm font-medium text-slate-700">
                      {item.nome}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
